import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { CommonService } from "src/app/shared/services/common.service";
import { LocalStorageSettingsService } from "src/app/shared/services/local-storage-settings.service";
import { ReportlinksserviceService } from "../../reportlinks/reportlinksservice.service";
import { forkJoin } from "rxjs";
import { MeddraBrowserComponent } from "../../jsignal-product/meddra-browser/meddra-browser.component";
import { MatDialog } from "@angular/material/dialog";
import { JsignalService } from "src/app/shared/services/jsignal.service";

// signal-fields.config.ts

interface SignalField {
  key: string;
  label: string;
  type: "text" | "textarea" | "select" | "date" | "custom" | "radio" | "checkbox";
  required?: boolean;
  stepsVisible?: number[];
  readOnlySteps?: number[];
  options?: any;
  lookupKey?: string;
  customConfig?: {
    dialogComponent: any; // Component to open
    valueField?: string; // Which field from dialog result to patch
    displayField?: string; // Optional display field
  };
  dependsOn?: {
    field: string;
    value: any;
  };
    // NEW LAYOUT PROPERTIES
  layout?: {
    row?: number;        // Which row (1-based)
    column?: number;      // Which column (1-based)
    colspan?: number;     // How many columns to span (default: 1)
    rowspan?: number;     // How many rows to span (default: 1)
  };
  sourceType?:String; // 'custom' for fields that get options from custom API
}

interface ValidationError {
  component: string;
  field?: string;
  message: string;
}

interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

@Component({
    selector: "app-signal-details",
    templateUrl: "./signal-details.component.html",
    styleUrls: ["./signal-details.component.scss"],
    standalone: false
})
export class SignalDetailsComponent implements OnInit {
  @Input() selectedSignal!: any;
  @Input() stepId!: number;
  @Input() signalId!: string;
  @Input() isEditMode!: boolean;

  @Output() saveAndNext = new EventEmitter<void>();

  form!: FormGroup;
  fields: SignalField[] = [];
  selectedMedicalConcept:any;
  // 🔹 ONLY non-TME dynamic fields here

  /**
   * @note : Any new field Add here must be added in BuildForm() with same Key name
   */
SIGNAL_FIELDS: SignalField[] = [
  // ROW 1: Signal Name (2 columns) + Medical Concept (1 column) signalId
    {
    key: "signalId",
    label: "Signal Id",
    type: "text",
    required: true,
    readOnlySteps: [1,2,3, 4],
    stepsVisible: [1, 2, 3, 4],
    layout: { row: 1, column: 1, colspan: 1 }
  },
  {
    key: "signalName",
    label: "Signal Name",
    type: "text",
    required: true,
    stepsVisible: [1, 2, 3, 4],
    layout: { row: 1, column: 1, colspan: 2 }
  },
  {
    key: "medicalConcept",
    label: "Medical Concept",
    type: "custom",
    required: true,
    stepsVisible: [2, 3, 4], // Only visible in steps 2,3,4
    readOnlySteps: [3, 4],
    layout: { row: 1, column: 1, colspan: 1 }
  },
  // {
  //   key: "medDRAVersion",
  //   label: "MedDRA Version",
  //   type: "text",
  //   stepsVisible: [2, 3, 4], // Only visible in steps 2,3,4
  //   readOnlySteps: [2,3, 4],
  //   layout: { row: 1, column: 1, colspan: 1 }
  // },

  // ROW 2: Signal Status + Primary Source + Secondary Source
  {
    key: "signalStatus",
    label: "Signal Status",
    type: "text",
    required: true,
    stepsVisible: [1, 2, 3, 4],
    readOnlySteps: [0, 1, 2, 3, 4],
    layout: { row: 2, column: 1, colspan: 1 }
  },
  {
    key: "primarySourceId",
    label: "Primary Source",
    type: "select",
    required: true,
    stepsVisible: [1, 2, 3, 4],
    
    layout: { row: 2, column: 2, colspan: 1 },
    sourceType:'custom',
  },
  {
    key: "secondarySourceId",
    label: "Secondary Source",
    type: "select",
    required: false,
    stepsVisible: [1, 2, 3, 4],
    
    layout: { row: 2, column: 3, colspan: 1 },
    sourceType:'custom',
  },

  // ROW 3: Description (full width)
  {
    key: "signalDescription",
    label: "Description",
    type: "textarea",
    required: true,
    stepsVisible: [1, 2, 3, 4],
    layout: { row: 3, column: 1, colspan: 3 }
  },

  // ROW 4: Date Fields Row 1
  {
    key: "signalDetectedDate",
    label: "Signal Detected Date",
    type: "date",
    stepsVisible: [1, 2, 3, 4],
    readOnlySteps: [2, 3, 4],
    layout: { row: 4, column: 1, colspan: 1 }
  },
  {
    key: "validationDueDate",
    label: "Validation Due Date",
    type: "date",
    stepsVisible: [1, 2, 3, 4],
    readOnlySteps: [1, 2, 3, 4],
    layout: { row: 4, column: 2, colspan: 1 }
  },
    {
    key: "validationCompletionDate",
    label: "Validation Completion Date",
    type: "date",
    stepsVisible: [3, 4], // Only visible in steps 3,4
    readOnlySteps: [1, 2, 3, 4],
    layout: { row: 4, column: 3, colspan: 1 }
  },


  // ROW 5: Date Fields Row 2
  {
    key: "assessmentDueDate",
    label: "Assessment Due Date",
    type: "date",
    stepsVisible: [3, 4], // Only visible in steps 3,4
    readOnlySteps: [1, 2, 3, 4],
    layout: { row: 5, column: 1, colspan: 1 }
  },
    {
    key: "assessmentCompletionDate",
    label: "Assessment CompletionDate",
    type: "date",
    stepsVisible: [4], // Only visible in step  4
    readOnlySteps: [1, 2, 3, 4],
    layout: { row: 5, column: 2, colspan: 1 }
  },

  // {
  //   key: "validationSlaDays",
  //   label: "Validation SLA (Days)",
  //   type: "select",
  //   required: false,
  //   stepsVisible: [1, 2], // Only visible in steps 1,2
  //   options: [
  //     { label: "15 Days", value: 15 },
  //     { label: "30 Days", value: 30 }
      
  //   ],
  //   layout: { row: 5, column: 3, colspan: 1 }
  // },


  // ROW 6: Signal Priority + Is TME
  {
    key: "signalPriority",
    label: "Signal Priority",
    type: "select",
    required: true,
    stepsVisible: [2, 3, 4], // Only visible in steps 2,3,4
    readOnlySteps: [3, 4],
    lookupKey: "signalPriority",
    layout: { row: 6, column: 1, colspan: 1 }
  },
  {
    key: "isTme",
    label: "Is TME",
    type: "radio",
    stepsVisible: [2, 3, 4], // Only visible in steps 2,3,4
    required: false,
    options: [
      { label: "Yes", value: true },
      { label: "No", value: false },
    ],
    layout: { row: 6, column: 2, colspan: 1 }
  },
    {
    key: "assignedUserName",
    label: "User Name",
    type: "text",
    required: true,
    stepsVisible: [3, 4], // Only visible in steps 3,4
    readOnlySteps: [3, 4],
    layout: { row: 6, column: 3, colspan: 1 }
  },
];
  // Add these properties to your component class
gridRows: { row: number; fields: SignalField[] }[] = [];
gridTemplateColumns: string = 'repeat(4, 1fr)';
medDRAVersion:any;
  constructor(
    private fb: FormBuilder,
    private commonService: CommonService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private spinner: NgxUiLoaderService,
    private jsignalsService: JsignalService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit() {
    console.log("Selected Signal in Details Component:", this.selectedSignal);
    this.buildForm();
    
    this.loadLookups();
    this.filterFieldsByStep();
    this.setupAutoCalculations();
      // View mode by default
  if (!this.isEditMode) {
    this.form.disable({ emitEvent: false });
  }
  }

ngOnChanges(changes: SimpleChanges) {
  if (!this.form) return;
  console.log(changes);

  if (changes["selectedSignal"] && this.selectedSignal) {
    this.patchValues();
      console.log(changes);
  }

  if (changes["stepId"] || changes["isEditMode"]) {
    this.filterFieldsByStep();
    this.updateTmeState();
      console.log(changes);
  }
}

private updateTmeState() {
  const isTme = this.form.get("isTme")?.value;
  const tmeGroup = this.form.get("tme");

  if (!tmeGroup) return;

  // TME editable only if BOTH conditions are true
  if (this.isEditMode && isTme) {
    tmeGroup.enable({ emitEvent: false });
  } else {
    tmeGroup.disable({ emitEvent: false });
  }
  this.cdr.detectChanges();
}

  buildForm() {
  this.form = this.fb.group({
    signalId: [""],
    signalName: [""],
    // type: [""],
    product: [""],
    signalStatus: [""],
    primarySourceId: [""],
    secondarySourceId: [""],
    signalPriority: [""],
    signalDescription: ["",[Validators.maxLength(5000)]],
    validationDueDate: [""],
    assessmentDueDate: [""],
    assessmentCompletionDate:[""],
    medicalConcept: [""],
    // medDRAVersion:[""],
    signalDetectedDate:[""],
    // validationSlaDays: [""],
    validationCompletionDate: [""],
    isTme: [false],
    assignedUserName:[''],

    tme: this.fb.group({
      tmeType: [""],
      tmeStartDate: [""],
      tmeEndDate: [""],
      additionalInfo: [""],
      monitoring: this.fb.group({
        guidedQuestion: [false],
        dedicatedEcrf: [false],
        aesiOngoingStudy: [false],
        reviewActivityPss: [false],
      }),
    }),
  });

  // React ONLY to TME radio change
  this.form.get("isTme")?.valueChanges.subscribe(() => {
    this.updateTmeState();
  });
}

filterFieldsByStep() {

  this.fields = this.SIGNAL_FIELDS.filter(f =>
    f.stepsVisible?.includes(this.stepId)
  );

  //   // Assign default layout to fields that don't have one
  this.assignDefaultLayouts();

  // // Rebuild grid layout
  // this.rebuildGridLayout();

  // Reorganize fields to remove empty rows
  this.reorganizeFieldsByStep();

  Object.keys(this.form.controls).forEach(key => {

    const control = this.form.get(key);
    if (!control) return;

    const field = this.fields.find(f => f.key === key);

    // If field not visible → disable
    if (!field) {
      control.disable({ emitEvent: false });
      return;
    }

    // View mode → disable everything
    if (!this.isEditMode) {
      control.disable({ emitEvent: false });
      return;
    }

    // Read-only step
    if (field.readOnlySteps?.includes(this.stepId)) {
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }

  });

}

  patchValues() {
    let parsedTme = null;

  if (this.selectedSignal.tme) {
    try {
      parsedTme = JSON.parse(this.selectedSignal.tme);
    } catch (e) {
      console.error('Invalid TME JSON', e);
    }
  }

  this.form.patchValue({
    ...this.selectedSignal,
    isTme: this.selectedSignal.isTme === true || this.selectedSignal.isTme === 'true',
    tme: parsedTme
  });
    // this.form.patchValue(this.selectedSignal);
  //     this.calculateValidationDueDate();
  // this.calculateAssessmentDueDate();
  }

  // loadLookups() {
  //   this.spinner.start();
  //   const lookupKeys = [
  //     ...new Set(
  //       this.SIGNAL_FIELDS.filter((f) => f.lookupKey).map((f) => f.lookupKey),
  //     ),
  //   ];

  //   const calls = lookupKeys.map((key) =>
  //     this.commonService.getlookupVauleByLookUpType(key),
  //   );

  //   forkJoin(calls).subscribe((results) => {
  //     results.forEach((res, i) => {
  //       this.SIGNAL_FIELDS.filter((f) => f.lookupKey === lookupKeys[i]).forEach(
  //         (f) => (f.options = res),
  //       );
  //     });
    
  //   });
  //   this.patchValues();
  //   this.spinner.stop();
  // }

saveDetails() {
  if (this.form.invalid) return;

  const updatedDetails = this.form.getRawValue();

  // const storageKey = `signal_${this.signalId}`;

  // // Get existing signal from localStorage (if exists)
  // const existing = localStorage.getItem(storageKey);
  // let existingSignal = existing ? JSON.parse(existing) : {};
  console.log(this.selectedMedicalConcept);
  let medicalConceptJson=undefined;
  if(this.selectedMedicalConcept){
    medicalConceptJson={
      name:this.selectedMedicalConcept.name,
      code:this.selectedMedicalConcept.code ? this.selectedMedicalConcept.code:null
    }
  }

  // Merge ONLY details
  const updatedSignal = {

    ...updatedDetails,        // overwrite only details fields
    isTme: !!updatedDetails.isTme,
    tme: updatedDetails.tme ? JSON.stringify(updatedDetails.tme) : null,
    "medicalConceptJson":medicalConceptJson
  };

  console.log("Selected signal Id in Save Function:", this.selectedSignal.id);

  console.log("Merged Signal Object to Save:", updatedSignal);

  // Save back to localStorage
  // localStorage.setItem(storageKey, JSON.stringify(updatedSignal));

  console.log("Saved Details", updatedSignal);

  this.updateSignal(this.selectedSignal.id,updatedSignal);
}

cancelEdit() {
  this.patchValues();
  this.form.disable({ emitEvent: false });
  this.updateTmeState();
}

  openCustomDialog(field: any) {
    const dialogRef = this.dialog.open(MeddraBrowserComponent, {
    width: '95vw',
    height: '95vh',
    maxWidth: '95vw',
    panelClass: 'meddra-dialog',
    disableClose: true,
    autoFocus: false,
        data: {
      mode: 'select'
    }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        console.log(result);
        
        this.selectedMedicalConcept=null;
        const item = result[0];
         this.selectedMedicalConcept=item;
        console.log('Dialog result:', item);
        this.form.get(field.key)?.setValue(item.name);
        //  this.form.get("medDRAVersion")?.setValue(item.medDRAVersion);
         this.medDRAVersion = item.medDRAVersion
      }
    });
  }

validateStep(): ValidationResult {
  const errors: ValidationError[] = [];

  // Loop only through fields visible in current step
  this.fields.forEach(field => {

    const control = this.form.get(field.key);
    if (!control) return;

    // Skip if not required
    if (!field.required) return;

    // If required & empty → error
    // if (control.disabled) return;

    if (
      control.value === null ||
      control.value === undefined ||
      control.value === '' ||
      (Array.isArray(control.value) && control.value.length === 0)
    ) {
      errors.push({
        component: "Details",
        field: field.key,
        message: `${field.label} is required`
      });

      control.markAsTouched();
    }

  });

  // 🔹 TME Field Validation
const isTme = this.form.get("isTme")?.value;

if (isTme && [2, 3, 4].includes(this.stepId)) {

  const tmeGroup = this.form.get("tme") as FormGroup;

  const requiredTmeFields = [
    { key: "tmeType", label: "TME Type" },
    { key: "tmeStartDate", label: "TME Start Date" },
    { key: "tmeEndDate", label: "TME End Date" }
  ];

  requiredTmeFields.forEach(field => {
    const control = tmeGroup.get(field.key);

    if (!control?.value) {
      errors.push({
        component: "Details",
        field: field.key,
        message: `${field.label} is required`
      });

      control?.markAsTouched();
    }
  });
}

  // 🔹 Special TME Monitoring validation
  if (!this.validateMonitoringCheckboxes()) {
    errors.push({
      component: "Details",
      message: "At least one Monitoring option must be selected for TME"
    });
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

private validateMonitoringCheckboxes() {
  const isTme = this.form.get("isTme")?.value;
  if (!isTme) return true;

  const monitoring = this.form.get("tme.monitoring");
  if (!monitoring) return false;

  return Object.values(monitoring.value).some(v => v === true);
}

private setupAutoCalculations() {

  // 🔹 Validation Due Date
  this.form.get('signalDetectedDate')?.valueChanges.subscribe(() => {
    this.calculateValidationDueDate();
  });

  this.form.get('validationSlaDays')?.valueChanges.subscribe(() => {
    this.calculateValidationDueDate();
  });

  // 🔹 Assessment Due Date
  this.form.get('validationCompletionDate')?.valueChanges.subscribe(() => {
    this.calculateAssessmentDueDate();
  });

  this.form.get('signalPriority')?.valueChanges.subscribe(() => {
    this.calculateAssessmentDueDate();
  });

}
private calculateValidationDueDate() {

  const detectedDate = this.form.get('signalDetectedDate')?.value;
  const slaValue = this.form.get('validationSlaDays')?.value;

  if (!detectedDate || !slaValue) return;

  // Handle object or number
  const slaDays = typeof slaValue === 'object'
      ? slaValue.lookupcode
      : slaValue;

    console.log("SLA Days for Validation Due Date Calculation:", slaDays);  

  if (!slaDays) return;

  const baseDate = new Date(detectedDate);
  const dueDate = new Date(baseDate);
  dueDate.setDate(baseDate.getDate() + Number(slaDays));

  this.form.get('validationDueDate')
      ?.setValue(dueDate, { emitEvent: false });
}

private calculateAssessmentDueDate() {

  const completionDate = this.form.get('validationCompletionDate')?.value;
  const priority = this.form.get('signalPriority')?.value;

  if (!completionDate || !priority) return;

  const priorityDaysMap: any = {
    Standard: 90,
    Emergency: 60,
    Urgent: 30
  };

  const daysToAdd = priorityDaysMap[priority];

  if (!daysToAdd) return;

  const baseDate = new Date(completionDate);
  const dueDate = new Date(baseDate);
  dueDate.setDate(baseDate.getDate() + daysToAdd);

  this.form.get('assessmentDueDate')
      ?.setValue(dueDate, { emitEvent: false });
}
// Add this method to organize fields by row
getFieldsByRow(): { row: number; fields: SignalField[] }[] {
  const rowMap = new Map<number, SignalField[]>();
  
  this.fields.forEach(field => {
    const row = field.layout?.row || 1; // Default to row 1 if not specified
    if (!rowMap.has(row)) {
      rowMap.set(row, []);
    }
    rowMap.get(row)!.push(field);
  });
  
  // Convert to array and sort by row number
  return Array.from(rowMap.entries())
    .map(([row, fields]) => ({
      row,
      fields: fields.sort((a, b) => (a.layout?.column || 1) - (b.layout?.column || 1))
    }))
    .sort((a, b) => a.row - b.row);
}

// Update getGridTemplateColumns to handle variable columns
getGridTemplateColumns(): string {
  if (this.fields.length === 0) return 'repeat(3, 1fr)';
  
  // Find maximum column needed across all visible fields
  let maxColumn = 3; // Default to 3 columns
  
  this.fields.forEach(field => {
    if (field.layout) {
      const col = field.layout.column || 1;
      const colspan = field.layout.colspan || 1;
      const fieldEndColumn = col + colspan - 1;
      if (fieldEndColumn > maxColumn) {
        maxColumn = fieldEndColumn;
      }
    }
  });
  
  return `repeat(${maxColumn}, 1fr)`;
}

// Safe getters with null checks
getGridColumnSpan(field: SignalField): string {
  if (!field.layout) return '1 / span 1';
  const column = field.layout.column || 1;
  const colspan = field.layout.colspan || 1;
  return `${column} / span ${colspan}`;
}

getGridRowSpan(field: SignalField): string {
  if (!field.layout) return '1 / span 1';
  const row = field.layout.row || 1;
  const rowspan = field.layout.rowspan || 1;
  return `${row} / span ${rowspan}`;
}

// Add this method to rebuild the grid layout
private rebuildGridLayout() {
  this.gridRows = this.getFieldsByRow();
  this.gridTemplateColumns = this.getGridTemplateColumns();
}

// Add this method to assign default layouts
private assignDefaultLayouts() {
  let currentRow = 1;
  let currentColumn = 1;
  const maxColumns = 3; // Default 3 columns

  this.fields.forEach(field => {
    if (!field.layout) {
      // Assign default layout
      field.layout = {
        row: currentRow,
        column: currentColumn,
        colspan: field.type === 'textarea' || field.type === 'custom' ? maxColumns : 1,
        rowspan: 1
      };

      // Update column position
      currentColumn += field.layout.colspan || 1;
      
      // Move to next row if we exceed max columns
      if (currentColumn > maxColumns) {
        currentRow++;
        currentColumn = 1;
      }
    }
  });
}

private reorganizeFieldsByStep() {

  const maxColumns = 3;

  const visibleFields = this.SIGNAL_FIELDS
    .filter(f => f.stepsVisible?.includes(this.stepId))
    .sort((a, b) => {
      const rowDiff = (a.layout?.row || 0) - (b.layout?.row || 0);
      if (rowDiff !== 0) return rowDiff;
      return (a.layout?.column || 0) - (b.layout?.column || 0);
    });

  const newGridRows: { row: number; fields: SignalField[] }[] = [];

  let currentRow = 1;
  let currentColumn = 1;

  newGridRows.push({ row: currentRow, fields: [] });

  visibleFields.forEach(field => {

    const colspan = field.layout?.colspan || 1;

    // If field doesn't fit in current row → go to next row
    if (currentColumn + colspan - 1 > maxColumns) {
      currentRow++;
      currentColumn = 1;
      newGridRows.push({ row: currentRow, fields: [] });
    }

    field.layout = {
      row: currentRow,
      column: currentColumn,
      colspan: colspan,
      rowspan: 1
    };

    newGridRows[newGridRows.length - 1].fields.push(field);

    currentColumn += colspan;
  });

  this.gridRows = newGridRows;
  this.gridTemplateColumns = `repeat(${maxColumns}, 1fr)`;
}

loadLookups() {
  this.loadStandardLookups();
  this.loadSignalSources();
  
}

// 🔹 Standard Lookup Loader
private loadStandardLookups() {
  this.spinner.start();
  const lookupFields = this.SIGNAL_FIELDS.filter(f => f.lookupKey);

  lookupFields.forEach(field => {
    this.commonService
      .getlookupVauleByLookUpType(field.lookupKey!)
      .subscribe({
        next: (res) => {
          const data = Array.isArray(res) ? res : [];

          field.options = data.map((item: any) => ({
            label: item.lookupvalue,
            value: item.lookupcode   // ✅ use code (better practice)
          }));
          this.spinner.stop();
          this.patchValues();
        },
        error: (err) => {
          console.error(`Lookup failed for ${field.lookupKey}`, err);
          field.options = [];
          this.spinner.stop();
        }
      });
  });
}

// 🔹 Custom API Loader
private loadSignalSources() {
  this.spinner.start();
  this.jsignalsService.getAllSignalSources().subscribe({
    next: (res) => {
      const data = Array.isArray(res) ? res : [];

      const options = data.map((item: any) => ({
        label: item.name,
        value: item.id
      }));

      this.SIGNAL_FIELDS
        .filter(f => f.sourceType === 'custom')
        .forEach(field => field.options = options);
        this.spinner.stop();
        this.patchValues();
    },
    error: (err) => {
      console.error('Signal Sources API failed', err);
       this.spinner.stop();
    }
  });
}

  updateSignal(id:any,signalData: any) {
    console.log("Updating signal with data:", signalData , id);
     this.spinner.start();
    this.jsignalsService
      .updateSignal(id,signalData)
      .subscribe({
        next: (res: any) => {
          console.log(res);
          if(res.error == true){
            this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
            this.spinner.stop();
            return;
          }
          this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
          this.spinner.stop();
          // this.loadGridData();
        },
        error: (err) => {
          console.error("error:", err);
          this.snackBar.open(err.error.statusMessage, "Ok", {
            duration: 3000,
          });
          this.spinner.stop();
          // this.loadGridData();
        },
      });  
  }

}
